import React, { useEffect, useRef, useState } from "react";
import "./IntervalTimePicker.css";

const digitsOnly = (value) => String(value || "").replace(/\D/g, "").slice(0, 4);

const clampHour = (h) => {
  if (h === "") return "";
  if (h.length === 1) return h;
  const n = Math.min(parseInt(h, 10) || 0, 23);
  return String(n).padStart(2, "0");
};

const clampMinute = (m) => {
  if (m === "") return "";
  if (m.length === 1) return m;
  const n = Math.min(parseInt(m, 10) || 0, 59);
  return String(n).padStart(2, "0");
};

export const isEmptyTime = (value) =>
  value == null ||
  value === "" ||
  value === "0:0" ||
  value === "00:00" ||
  value === "0:00" ||
  value === "00:0";

const toDisplay = (value) => {
  if (isEmptyTime(value)) return "";
  const d = digitsOnly(value);
  if (d.length === 0) return "";
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}:${d.slice(2, 4)}`;
};

const toEmitValue = (display) => {
  const d = digitsOnly(display);
  if (d.length < 4) return null;
  const hour = clampHour(d.slice(0, 2));
  const minute = clampMinute(d.slice(2, 4));
  if (hour.length < 2 || minute.length < 2) return null;
  return `${hour}:${minute}`;
};

const IntervalTimePicker = (props) => {
  const [text, setText] = useState(toDisplay(props.value));
  const inputRef = useRef(null);

  useEffect(() => {
    setText(toDisplay(props.value));
  }, [props.value]);

  useEffect(() => {
    if (props.focusTrigger && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(0, 2);
      }, 80);
    }
  }, [props.focusTrigger]);

  const emitIfComplete = (display) => {
    if (!props.onChange) return;
    const formatted = toEmitValue(display);
    if (formatted) props.onChange(formatted);
  };

  const onChange = (event) => {
    const prevDigits = digitsOnly(text);
    const rawDigits = digitsOnly(event.target.value);
    let hour = rawDigits.slice(0, 2);
    let minute = rawDigits.slice(2, 4);

    if (hour.length === 2) hour = clampHour(hour);
    if (minute.length === 2) minute = clampMinute(minute);

    let next = hour;
    if (rawDigits.length > 2) {
      next = `${hour}:${minute}`;
    }

    setText(next);
    emitIfComplete(next);

    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const caret =
        rawDigits.length <= 2 ? rawDigits.length : rawDigits.length + 1;
      const pos = Math.min(caret, next.length);
      inputRef.current.setSelectionRange(pos, pos);
    });

    if (prevDigits.length === 1 && rawDigits.length === 2) {
      requestAnimationFrame(() => {
        if (!inputRef.current) return;
        inputRef.current.setSelectionRange(3, 3);
      });
    }
  };

  const onBlur = () => {
    const d = digitsOnly(text);
    if (d.length === 0) {
      setText("");
      return;
    }
    let hour = d.slice(0, 2);
    let minute = d.slice(2, 4);
    if (hour.length === 1) hour = hour.padStart(2, "0");
    if (minute.length === 1) minute = minute.padStart(2, "0");
    hour = clampHour(hour);
    minute = minute === "" ? "" : clampMinute(minute);

    if (hour.length === 2 && minute.length === 2) {
      const formatted = `${hour}:${minute}`;
      setText(formatted);
      if (props.onChange) props.onChange(formatted);
      return;
    }

    setText(minute ? `${hour}:${minute}` : hour);
  };

  return (
    <div className={`col-md-${props.col} mb-3`}>
      <div className="interval-floatLabel-time">
        <div>
          <div
            className={["interval-time-picker", props.className]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              ref={inputRef}
              className="interval-time-input"
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={text}
              placeholder="--:--"
              onChange={onChange}
              onBlur={onBlur}
              aria-label={props.placeHolder || "time"}
            />
          </div>
          <label className={text !== "" ? "interval-floatLabel-timeWhenValue" : ""}>
            {props.placeHolder}
          </label>
        </div>
      </div>
    </div>
  );
};

export default IntervalTimePicker;
